import React, { useEffect, useMemo, useState } from 'react'
import { Box, Button, Grid, Paper, Table, TableBody, TableCell, TableHead, TableRow, TableSortLabel } from '@mui/material';
import { LeasesBasicDetails,  LeasesNav } from '../../elements';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import { FilterComponent, NoRecordFound, PageLoader, Pagination, STableLoader } from '../../../shared_elements';
import ClaimManagementListing from '../components/ClaimManagementListing';
import { globalGetService } from '../../../utils/globalApiServices';
import { checkApiStatus } from '../../../utils';
import { useParams } from 'react-router';
import { exportClaimManagementApi,  getAssetDropdowmListApi, getClaimManagementListApi } from '../apiService';
import { trackActivity } from '../../../utils/mixpanel';
import { enqueueSnackbar } from 'notistack';
import { ButtonLoader } from '../../../shared_elements/components';
import { RoleBaseAccess } from '../../../hoc';
import { useSelector } from 'react-redux';
import ModuleNotExcessModal from '../../../shared_elements/components/ModuleNotExcessModal';
import BackToLeaseList from '../../elements/BackToLeaseList';

const TABLE_HEIGHT = 'calc(100vh - 215px)';

const claimStatusOptions = [
  { label: 'Submitted', value: 1 },
  { label: 'In-Review', value: 2 },
  { label: 'Approved', value: 3 },
  { label: 'Rejected', value: 4 },
  { label: 'Disbursement Verification', value: 5 },
  { label: 'Paid', value: 6 },
];



const ClaimManagementHd = ({ sortInfo, createSortHandler }) => {
  const handleItemClicked = (page_type) => {
    trackActivity('Item Clicked', { page_title: `Claim Management`, page_type: page_type, })
  }
  const tableSorting = (key, name) => {
    return (
      <TableSortLabel
        active={sortInfo.sort_by === key ? true : false}
        direction={sortInfo.sort_by === key ? sortInfo.sort : 'asc'}
        onClick={() => { createSortHandler(key); handleItemClicked(`Table Sorting: ${name}`) }}
      >
        {name}
      </TableSortLabel>
    )
  }
  return (
    <TableHead>
      <TableRow>
        <TableCell className="table-actions-header" sx={{ minWidth: "60px" }}>Action(s)</TableCell>
        <TableCell sx={{ minWidth: "110px" }}>Assembly</TableCell>
        <TableCell sx={{ minWidth: "140px" }}> Event/Shop Visit</TableCell>
        <TableCell sx={{ minWidth: "120px" }}> {tableSorting('submission_date', 'Submission Date')}</TableCell>
        <TableCell sx={{ minWidth: "110px" }}> {tableSorting('shop_visit_cost', 'SV Cost')}</TableCell>
        <TableCell sx={{ minWidth: "120px" }}> {tableSorting('status_label', 'Claim Status')}</TableCell>
        <TableCell sx={{ minWidth: "120px" }}>Claim Amount</TableCell>
        <TableCell sx={{ minWidth: "120px" }}>Approved Amount</TableCell>
      </TableRow>
    </TableHead>
  )
}

const ClaimManagement = () => {
  const { leaseSlug, } = useParams();
  const { leaseBasicDetails } = useSelector((state) => state.leases);
  const [skeletonLoader, setSkeletonLoader] = useState(false);
  const [claimManagementData, setClaimManagementData] = useState();
  const [isLoading, setLoading] = useState(false);
  const [sortInfo, setSortInfo] = useState({ sort: '', sort_by: '' });
  const [isExport, setExport] = useState();
  const [filter, setFilter] = useState({});
  const [assemblyOptions, setAssemblyOptions] = useState([]);
  const [drawerAssetType, setDrawerAssetType] = useState(undefined);
  const [queryConfig, setQueryConfig] = useState({
    page: claimManagementData?.pagination?.page || 1,
    per_page: claimManagementData?.pagination?.per_page || 50,
  });

  useEffect(() => {
    setDrawerAssetType(undefined);
  }, [filter.asset_type]);

  const claimManagementFilterMenu = useMemo(() => {
    const activeAssetType = drawerAssetType === undefined ? filter.asset_type : drawerAssetType;
    const filteredAssemblyOptions = activeAssetType
      ? assemblyOptions.filter(item => item.asset_type === activeAssetType)
      : assemblyOptions;
    return {
      // asset_type: { title: 'Asset Type', inputType: 'dropdown', options: assetType, valueKey: 'value', labelKey: 'label', multiple: false, dependentFields: ['major_assembly'] },
      // major_assembly: { title: 'Assembly', inputType: 'dropdown', options: filteredAssemblyOptions, valueKey: 'value', labelKey: 'asset_name', multiple: false },
      status: { title: 'Claim Status', inputType: 'dropdown', options: claimStatusOptions, valueKey: 'value', labelKey: 'label', multiple: false },
      start_date: { title: 'Claim Start Date', inputType: 'date' },
      end_date: { title: 'Claim End Date', inputType: 'date' },
      onDependencyChange: (keyParam, value) => {
        if (keyParam === 'asset_type') setDrawerAssetType(value != null ? value : null);
      },
      onOpen: () => setDrawerAssetType(undefined),
    };
  }, [assemblyOptions, filter.asset_type, drawerAssetType]);

  const sanitizeFilter = (filterObj) =>
    Object.fromEntries(Object.entries(filterObj).filter(([, v]) => v !== null && v !== undefined && v !== ''));

  const buildApiQuery = (activeFilter) => {
    const { status, ...rest } = activeFilter;
    if (status === 5 || status === 6) {
      return { ...rest, payout_status: status };
    }
    return status !== undefined ? { ...rest, status } : rest;
  };

  useEffect(() => {
    trackActivity('Page Viewed', { page_title: 'Claim Management' });
    globalGetService(getAssetDropdowmListApi()).then(response => {
      if (checkApiStatus(response)) {
        setAssemblyOptions((response?.data?.data || []).map(item => ({
          ...item,
          value: `[(${item.asset_id},${item.asset_type})]`,
        })));
      }
    });
  }, [])
  const getClaimManagementList = (query = {}, loaderType) => {
    loaderType === 'skeletonLoader' ? setSkeletonLoader(true) : setLoading(true);
    globalGetService(getClaimManagementListApi(leaseSlug, query)).then(response => {
      if (checkApiStatus(response)) {
        setClaimManagementData(response?.data?.data);
      } else {
        enqueueSnackbar(response?.data?.message || 'Something went wrong', { variant: "error", anchorOrigin: { vertical: "top", horizontal: "right" } });
      }
      loaderType === 'skeletonLoader' ? setSkeletonLoader(false) : setLoading(false);
    }).catch(() => {
      enqueueSnackbar('Something went wrong', { variant: "error", anchorOrigin: { vertical: "top", horizontal: "right" } });
      loaderType === 'skeletonLoader' ? setSkeletonLoader(false) : setLoading(false);
    })
  }
  useEffect(() => {
    if (leaseSlug) {
      if (!leaseBasicDetails) return;
      if (leaseBasicDetails?.status === 'Draft') return;
    }
    getClaimManagementList(queryConfig, 'skeletonLoader',);
  }, [leaseSlug,leaseBasicDetails?.status])

  const handleFilterChange = (updatedFilter) => {
    const sanitized = sanitizeFilter(updatedFilter);
    setFilter(sanitized);
    getClaimManagementList({
      ...buildApiQuery(sanitized),
      page: 1,
      per_page: claimManagementData?.pagination?.per_page || 50,
    }, 'pageLoader');
  };

  const createSortHandler = (key) => {
    const isAsc = queryConfig.sort_by === key && queryConfig.sort === 'asc';
    const updatedSort = isAsc ? 'desc' : 'asc';
    const updatedConfig = {
      page: claimManagementData?.pagination?.page || 1,
      per_page: claimManagementData?.pagination?.per_page || 20,
      sort_by: key,
      sort: updatedSort,
    };
    setQueryConfig(updatedConfig);
    getClaimManagementList({ ...buildApiQuery(filter), ...updatedConfig }, '');
  };
  const handleExport = () => {
    const query =  { lease_slug: leaseSlug, ...buildApiQuery(filter), ...queryConfig } 
    setExport(true);
    globalGetService(exportClaimManagementApi(query)).then((response) => {
      if (checkApiStatus(response)) {
        enqueueSnackbar(response?.data?.message, { variant: "success", anchorOrigin: { vertical: "top", horizontal: "right" } });
      }
      setExport(false);
    })
  }
  return (<>
    <LeasesBasicDetails />
    <Paper className='lease-details-section lease-nav-module-margin' elevation={0} sx={{ boxShadow: "0px 0px 2px 1px #BBBBBB" }} square>
       <LeasesNav />
      {leaseBasicDetails?.status === 'Draft' ? (<ModuleNotExcessModal moduleName="Claim Management" />) : leaseBasicDetails && Object.keys(leaseBasicDetails).length > 0 && !leaseBasicDetails?.status ? (<ModuleNotExcessModal leaseNotFound />) :
        <div className='lease-details-content'>
          <Paper square className="responsive-paper">
            <Grid container spacing={1} className="responsive-grid" alignItems='end'>
               <Grid item size={{ md: 12, xs: 12 }} sx={{ marginBottom:"-15px", marginLeft: "3px" }} className="menu-container lease-list-link-mt">
                <BackToLeaseList />
              </Grid>
             
              <Grid md={4} xs={12} size='grow' sx={{ minWidth: 0, overflow: 'hidden' }}>
                {(claimManagementData?.list?.length > 0 || Object.keys(filter).length > 0) ? <Box display="flex" alignItems="center" gap={1} sx={{ marginTop: '5px', marginLeft: "-2px" }} flexShrink={0}><FilterComponent filter={filter} filterMenu={claimManagementFilterMenu} getResponseBack={handleFilterChange} /></Box> : null}
              </Grid>
              <Grid item md={8} xs={12}>
                <ul className='list-inline lease-details-right-items lease-details-btn' style={{ padding: '0px', float: 'right', minHeight: '35px' }}>
                  {claimManagementData?.list?.length > 0 ? <li className='lease-mobile-button'> <Button startIcon={isExport ? <ButtonLoader size={15} /> : <ArrowDownwardIcon />} variant="outlined" size='small' className='btn-primary action-btn-start-icon' onClick={() => {
                    handleExport(); trackActivity('Item Clicked', { page_title: `Claim Management`, page_type: 'Clicked on Export Button', })
                  }} sx={{ top: "5px" }}> Export </Button> </li> : null}
                </ul>
              </Grid>
            </Grid>

          </Paper>
          <Paper className='lease-detail-table table-container lease-module-paper' elevation={0} sx={{ display: 'flex', flexDirection: 'column', maxHeight: TABLE_HEIGHT, height: claimManagementData?.list?.length > 0 ? `${TABLE_HEIGHT} !important` : 'auto !important' }}>
            <div style={{ flex: '1 1 auto', overflow: 'auto', minHeight: 0 }}>
              {skeletonLoader ? <STableLoader count={10} rowCount={11} /> : (
                <Table className="mui-table-format" stickyHeader>
                  <ClaimManagementHd sortInfo={sortInfo} createSortHandler={createSortHandler} />
                  <TableBody>
                    {claimManagementData?.list?.length > 0 && claimManagementData?.list?.map((item, index) => {
                      return (
                        <ClaimManagementListing
                          item={item}
                          key={index}
                          onView={() => window.open(`/lease/${item?.lease?.slug ? item?.lease?.slug : leaseSlug }/shop-visit/view/${item?.shop_visit?.slug}`, "_blank", "noopener,noreferrer" ) }
                        />
                      )
                    })}
                  </TableBody>
                </Table>
              )}
              {!skeletonLoader && !claimManagementData?.list?.length && <NoRecordFound />}
            </div>
            <Paper>
              <Pagination
                pagination={claimManagementData?.pagination}
                onPageChange={(event, newPage) => getClaimManagementList({ ...buildApiQuery(filter), ...queryConfig, page: newPage + 1, per_page: claimManagementData?.pagination?.per_page }, 'pageLoader')}
                onRowsPerPageChange={(e) => getClaimManagementList({ ...buildApiQuery(filter), ...queryConfig, page: 1, per_page: e.target.value }, 'pageLoader')}
              />
            </Paper>
          </Paper>
        </div>}

    </Paper>
    {isLoading ? <PageLoader /> : null}
  </>
  )
}

export default RoleBaseAccess(ClaimManagement, { application: 'lease_revamp', feature: 'maintenance_claims', operationType: ['R'], leaseNav: true });